import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Session, User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { UserRole, Profile } from '@/types/database.types';
import { queryClient } from '@/lib/query-client';

export type { UserRole, Profile };

interface AuthContextType {
    session: Session | null;
    user: User | null;
    profile: Profile | null;
    isLoading: boolean;
    isSeller: boolean;
    isBuyer: boolean;
    isAdmin: boolean;
    isBanned: boolean;
    isPaused: boolean;
    signOut: () => Promise<void>;
    refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [session, setSession] = useState<Session | null>(null);
    const [user, setUser] = useState<User | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;

        // 1. Initialize from Persistent Storage
        const initSession = async () => {
            const { data: { session: existingSession } } = await supabase.auth.getSession();
            if (mountedRef.current) {
                if (existingSession) {
                    setSession(existingSession);
                    setUser(existingSession.user);
                    // Fetch profile silently in background
                    fetchProfile(existingSession.user.id);
                } else {
                    setIsLoading(false);
                }
            }
        };

        initSession();

        // 2. State Change Listener (The 'Atlas' Way)
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, newSession) => {
            if (!mountedRef.current) return;

            console.log(`[SSC Auth] Event: ${event}`);

            // Immediately set basic user info to prevent 'not logged in' state
            setSession(newSession);
            setUser(newSession?.user ?? null);

            // Cleanup URL hash if it looks like a Supabase redirect to prevent stale session errors on refresh
            if (event === 'SIGNED_IN' || event === 'PASSWORD_RECOVERY') {
                const url = new URL(window.location.href);
                if (url.hash || url.searchParams.has('access_token') || url.searchParams.has('code')) {
                    console.log('[SSC Auth] Cleaning up stale auth tokens from URL');
                    window.history.replaceState(null, '', url.pathname + url.search);
                }
            }

            if (newSession?.user) {
                fetchProfile(newSession.user.id);
            } else {
                setProfile(null);
                setIsLoading(false);
            }
        });

        return () => {
            mountedRef.current = false;
            subscription.unsubscribe();
        };
    }, []);

    const fetchProfile = async (id: string) => {
        try {
            const { data } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', id)
                .maybeSingle();

            if (mountedRef.current) {
                if (data) {
                    setProfile(data as Profile);
                }
            }
        } catch (err) {
            console.error('[SSC Auth] Profile fetch error:', err);
        } finally {
            if (mountedRef.current) setIsLoading(false);
        }
    };

    const signOut = async () => {
        setIsLoading(true);
        try {
            await supabase.auth.signOut();
            queryClient.clear(); // Clear all context on signout
        } finally {
            setIsLoading(false);
        }
    };

    const refreshProfile = () => fetchProfile(user?.id || '');

    // Derived helpers
    const isAdmin = profile?.role === 'admin';
    const isSeller = profile?.role === 'seller' || profile?.role === 'admin';
    const isBuyer = profile?.role === 'buyer';
    const isBanned = profile?.is_banned === true;
    const isPaused = profile?.is_paused === true;

    return (
        <AuthContext.Provider value={{
            session,
            user,
            profile,
            isLoading,
            isSeller,
            isBuyer,
            isAdmin,
            isBanned,
            isPaused,
            signOut,
            refreshProfile
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
